import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSessionKeepAlive } from '@/hooks/useSessionKeepAlive';
import { RoleRouter } from '@/router';
import AdminRoute from '@/components/AdminRoute';
import ProtectedRoute from '@/components/ProtectedRoute';
import ActiveSiteSync from '@/components/ActiveSiteSync';
import ImpersonationRibbon from '@/components/ImpersonationRibbon';
// Eager: auth + landing — first paint on the common paths, no Suspense flash.
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import PasswordResetPage from '@/pages/PasswordResetPage';
import DashboardPage from '@/pages/DashboardPage';
import SuperAdminRoute from '@/components/SuperAdminRoute';

// Lazy: every other route loads on demand — keeps the studio editor (~305KB),
// recharts (~360KB), admin/root, and the intake flow OUT of the initial bundle.
const BuildWizard = lazy(() => import('@/pages/BuildWizard'));
const WelcomeBackPage = lazy(() => import('@/pages/WelcomeBackPage'));
const Start = lazy(() => import('@/pages/Start'));
const PathSelection = lazy(() => import('@/pages/intake/PathSelection'));
const StaffIntakeWizard = lazy(() => import('@/pages/intake/StaffIntakeWizard'));
const SelfServeIntake = lazy(() => import('@/pages/intake/SelfServeIntake'));
const IntakeSummary = lazy(() => import('@/pages/intake/IntakeSummary'));
const IntakePayment = lazy(() => import('@/pages/intake/IntakePayment'));
const IntakeConfirmation = lazy(() => import('@/pages/intake/IntakeConfirmation'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const AiUsagePage = lazy(() => import('@/pages/AiUsagePage'));
const LeadsPage = lazy(() => import('@/pages/LeadsPage'));
const HelpCenterPage = lazy(() => import('@/pages/HelpCenterPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'));
const PickALookPage = lazy(() => import('@/pages/PickALookPage'));
const CardStylePage = lazy(() => import('@/pages/CardStylePage'));
const SettingsDomainPage = lazy(() => import('@/pages/SettingsDomainPage'));
const SettingsIntegrationsPage = lazy(() => import('@/pages/SettingsIntegrationsPage'));
const SettingsTeamPage = lazy(() => import('@/pages/SettingsTeamPage'));
const SettingsMemoryPage = lazy(() => import('@/pages/SettingsMemoryPage'));
const LeadRoutingPage = lazy(() => import('@/pages/LeadRoutingPage'));
const Preview = lazy(() => import('@/pages/Preview'));
const AdminSitesPage = lazy(() => import('@/pages/AdminSitesPage'));
const AdminSiteDetailPage = lazy(() => import('@/pages/AdminSiteDetailPage'));
const AdminTemplateUploadPage = lazy(() => import('@/pages/AdminTemplateUploadPage'));
const AdminLegalDocumentsPage = lazy(() => import('@/pages/AdminLegalDocumentsPage'));
const AdminMlsCatalogPage = lazy(() => import('@/pages/AdminMlsCatalogPage'));
const AdminSystemStatusPage = lazy(() => import('@/pages/AdminSystemStatusPage'));
const AdminPaymentsPage = lazy(() => import('@/pages/AdminPaymentsPage'));
const AdminAccountBillingPage = lazy(() => import('@/pages/AdminAccountBillingPage'));
const MlsWizard = lazy(() => import('@/pages/MlsWizard'));
const MyStateMlsMemberPage = lazy(() => import('@/pages/MyStateMlsMemberPage'));
const AdminAiSpendPage = lazy(() => import('@/pages/AdminAiSpendPage'));
const AdminScoringPage = lazy(() => import('@/pages/AdminScoringPage'));
const AdminChatDeadEndsPage = lazy(() => import('@/pages/AdminChatDeadEndsPage'));
const RootUsers = lazy(() => import('@/pages/root/RootUsers'));
const RootUserDetail = lazy(() => import('@/pages/root/RootUserDetail'));
const RootWebsites = lazy(() => import('@/pages/root/RootWebsites'));
const RootTemplates = lazy(() => import('@/pages/root/RootTemplates'));
const RootPayments = lazy(() => import('@/pages/root/RootPayments'));
const RootWallets = lazy(() => import('@/pages/root/RootWallets'));
const RootSiteLooks = lazy(() => import('@/pages/root/RootSiteLooks'));
const RootPlans = lazy(() => import('@/pages/root/RootPlans'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ApiKeysPage = lazy(() => import('@/pages/ApiKeysPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'));
const IntegrationDetailPage = lazy(() => import('@/pages/IntegrationDetailPage'));
const BrandListPage = lazy(() => import('@/pages/BrandListPage'));
const BrandEditorPage = lazy(() => import('@/pages/BrandEditorPage'));
const BusinessInfoPage = lazy(() => import('@/pages/BusinessInfoPage'));
const FooterLinksPage = lazy(() => import('@/pages/FooterLinksPage'));
const ChangeRequestsPage = lazy(() => import('@/pages/ChangeRequestsPage'));
const RootChangeRequests = lazy(() => import('@/pages/root/RootChangeRequests'));
const BuilderRedirect = lazy(() => import('@/pages/BuilderRedirect'));
const StudioBuilder = lazy(() => import('@/pages/StudioBuilder'));
const BlogWorkspacePage = lazy(() => import('@/pages/BlogWorkspacePage'));
const AutopilotPage = lazy(() => import('@/pages/AutopilotPage'));
const AiMarketerPage = lazy(() => import('@/pages/AiMarketerPage'));
const ListingEntryPage = lazy(() => import('@/pages/ListingEntryPage'));
const ListingEntryWizardPage = lazy(() => import('@/pages/ListingEntryWizardPage'));
const CommunitiesPage = lazy(() => import('@/pages/CommunitiesPage'));
const LeadDetailPage = lazy(() => import('@/pages/LeadDetailPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));
const JoinBrokerPage = lazy(() => import('@/pages/JoinBrokerPage'));
const ConfirmCancelPage = lazy(() => import('@/pages/ConfirmCancelPage'));
const WelcomePage = lazy(() => import('@/pages/WelcomePage'));
const PublicLegalPage = lazy(() => import('@/pages/PublicLegalPage'));
const OfficesWorkspacePage = lazy(() => import('@/pages/OfficesWorkspacePage'));
const MySitesPage = lazy(() => import('@/pages/MySitesPage'));
const BlocksDemo = lazy(() => import('@/pages/BlocksDemo'));
import { Toaster } from '@/components/ui/toaster';

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Hydrates the session from the httpOnly cookie (GET /auth/me) exactly once on load,
 * and holds a loading shell ABOVE the Router until it resolves. Without this gate the
 * app would flash the login page on every reload before hydration completes (cookie
 * auth has no synchronous JS token to read at startup).
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // Proactively keep the session alive so long edits / stepping away don't log out.
  useSessionKeepAlive();

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-surface">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-brand-border border-t-brand-plum"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  return <>{children}</>;
}

/** Shown briefly while a lazy route chunk loads (only on first visit to each route —
 *  chunks are cached after). Matches the AuthGate spinner so navigation feels seamless. */
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-surface">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-brand-border border-t-brand-plum"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

export default function App() {
  // Tenant AND user: impersonating a teammate in the same tenant is still a different account.
  const tenantKey = useAuthStore((s) => `${s.tenantId ?? 'anon'}:${s.userId ?? 'anon'}`);
  return (
    <AuthGate>
    <BrowserRouter>
      <ActiveSiteSync />
      {/* Above every route, inside the router so it can navigate. Renders nothing unless an
          admin is impersonating — see components/ImpersonationRibbon.tsx for why it is a band
          that takes space rather than a badge that can be missed. */}
      <ImpersonationRibbon />
      {/* Keyed on the account so a change of account REMOUNTS every routed page.
          Clearing stores and caches is not enough on its own: a page already on screen keeps its
          own `useState` — the Blog workspace's draft, the wizard's progress — and writes it back
          on the next edit, now under the new account. Impersonation swaps identity without any
          navigation, so nothing would otherwise unmount. AuthGate holds the tree until hydrate
          resolves, so this key is already set on first paint and does not remount at startup. */}
      <Suspense key={tenantKey} fallback={<RouteFallback />}>
      <Routes>
        {/* Accept team invite — fully public */}
        <Route path="/invite" element={<AcceptInvitePage />} />
        <Route path="/join-broker" element={<JoinBrokerPage />} />
        <Route path="/confirm-cancel" element={<ConfirmCancelPage />} />

        {/* Wave 5 block-renderer demo — fully public (no auth) for smoke-testing */}
        <Route path="/blocks-demo" element={<BlocksDemo />} />

        {/* Public routes */}
        <Route
          path="/login"
          element={
            <RedirectIfAuth>
              <LoginPage />
            </RedirectIfAuth>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuth>
              <RegisterPage />
            </RedirectIfAuth>
          }
        />
        <Route
          path="/password-reset"
          element={
            <RedirectIfAuth>
              <PasswordResetPage />
            </RedirectIfAuth>
          }
        />

        {/* Intake wizard — fully public, no auth required.
            These routes must remain outside any ProtectedRoute or RoleRouter group.
            See router.tsx for the canonical note on why. */}
        <Route path="/start" element={<Start />} />
        <Route path="/start/path" element={<PathSelection />} />
        <Route path="/start/staff/step/:step" element={<StaffIntakeWizard />} />
        <Route path="/start/self" element={<SelfServeIntake />} />
        <Route path="/start/summary" element={<IntakeSummary />} />
        <Route path="/start/payment" element={<IntakePayment />} />
        <Route path="/start/confirmation" element={<IntakeConfirmation />} />
        {/* Wildcard catch for any unrecognised /start/... path — stays public */}
        <Route path="/start/*" element={<Navigate to="/start" replace />} />

        {/* Retired setup screens (onboarding, the old setup wizard, site creation and template
            wizards). Setup has one door, the /build wizard; old links and bookmarks land there.
            /build resumes where the agent left off. */}
        <Route path="/onboarding" element={<Navigate to="/build" replace />} />
        <Route path="/setup" element={<Navigate to="/build" replace />} />
        <Route path="/wizard" element={<Navigate to="/build" replace />} />
        <Route path="/template-wizard" element={<Navigate to="/build?step=looks" replace />} />

        {/* Build Wizard — first-run guided build (site → brand → template → looks → pages →
            content → integrations → Studio). Full-screen, resumable, re-runnable. */}
        <Route
          path="/build"
          element={
            <ProtectedRoute>
              <BuildWizard />
            </ProtectedRoute>
          }
        />

        {/* MyStateMLS button, member who already has a site: where they left off, or the dashboard. */}
        <Route
          path="/welcome-back"
          element={
            <ProtectedRoute>
              <WelcomeBackPage />
            </ProtectedRoute>
          }
        />

        {/* MLS wizard — pick your MLS, get credentials, connect. Full-screen and resumable:
            credentials take days to arrive, so this is never one sitting. */}
        <Route
          path="/connect-mls"
          element={
            <ProtectedRoute>
              <MlsWizard />
            </ProtectedRoute>
          }
        />

        {/* MyStateMLS member quick-connect — client id + secret, checked, then state + office id.
            One card instead of the four-step wizard, and never a base URL. */}
        <Route
          path="/connect-mls/mystatemls"
          element={
            <ProtectedRoute>
              <MyStateMlsMemberPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <RoleRouter />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ai-usage" element={<AiUsagePage />} />
          <Route path="/sites" element={<MySitesPage />} />
          <Route path="/billing" element={<BillingPage />} />
          {/* The legacy builder is retired in full — the Studio is the single builder.
              /builder/design (BuilderPage: Canvas component model, DesignPanel, PageSelector,
              SettingsPanel) is no longer routed; its edits only ever persisted through the
              whole-blob site-config autosave, and its component model never survived a reload
              at all (GET /pages reads site_pages, which has no `components` column, so every
              load reset page.components to []). Redirect rather than 404 so old bookmarks and
              deep links land somewhere useful. */}
          <Route path="/builder" element={<BuilderRedirect />} />
          <Route path="/builder/design" element={<Navigate to="/studio" replace />} />
          <Route path="/builder/design/:page_id" element={<Navigate to="/studio" replace />} />
          {/* Phase F — unified Studio builder. Blog + Communities are combined in as
              Studio sub-workspaces (the standalone PlanModePage surfaces are retired). */}
          <Route path="/studio" element={<StudioBuilder />} />
          <Route path="/studio/blog" element={<BlogWorkspacePage />} />
          <Route path="/studio/communities" element={<CommunitiesPage />} />
          {/* Content authoring is now handled by the Studio's block editor. */}
          <Route path="/content" element={<Navigate to="/studio" replace />} />
          <Route path="/blog" element={<Navigate to="/studio/blog" replace />} />
          <Route path="/communities" element={<Navigate to="/studio/communities" replace />} />
          <Route path="/ai-marketer" element={<AiMarketerPage />} />
          <Route path="/listing-entry" element={<ListingEntryPage />} />
          <Route path="/listing-entry/:id" element={<ListingEntryWizardPage />} />
          <Route path="/autopilot" element={<AutopilotPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/help/:articleId" element={<HelpCenterPage />} />
          <Route path="/leads/:id" element={<LeadDetailPage />} />
          <Route path="/agents" element={<Navigate to="/team?view=agents" replace />} />
          <Route path="/offices" element={<OfficesWorkspacePage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/domains" element={<SettingsDomainPage />} />
          {/* Old sidebar Preview page iframed the account's first site, live only. Preview is now
              the Studio's preview mode: the active site's draft, so old links land there. */}
          <Route path="/preview" element={<Navigate to="/studio?view=preview" replace />} />
          <Route path="/preview/:token" element={<Preview />} />
          <Route path="/go-live" element={<SettingsDomainPage entryPoint="go-live" />} />
          <Route path="/account" element={<ProfilePage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/looks" element={<PickALookPage />} />
          <Route path="/cards" element={<CardStylePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/domain" element={<SettingsDomainPage />} />
          <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
          <Route path="/settings/team" element={<SettingsTeamPage />} />
          <Route path="/settings/memory" element={<SettingsMemoryPage />} />
          <Route path="/settings/lead-routing" element={<LeadRoutingPage />} />
          <Route path="/community" element={<Navigate to="/studio/communities" replace />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/api-keys" element={<ApiKeysPage />} />
          <Route path="/settings/legal" element={<LegalPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/integrations/:id" element={<IntegrationDetailPage />} />
          <Route path="/brands" element={<BrandListPage />} />
          <Route path="/brands/:id" element={<BrandEditorPage />} />
          <Route path="/business-info" element={<BusinessInfoPage />} />
          <Route path="/footer-links" element={<FooterLinksPage />} />
          <Route path="/requests" element={<ChangeRequestsPage />} />
          <Route path="/agent/status" element={<SettingsDomainPage entryPoint="go-live" />} />

          {/* Admin routes — inside AppLayout so sidebar is visible */}
          <Route path="/admin/sites" element={<AdminRoute><AdminSitesPage /></AdminRoute>} />
          <Route path="/admin/sites/:slug" element={<AdminRoute><AdminSiteDetailPage /></AdminRoute>} />
          <Route path="/admin/ai-spend" element={<AdminRoute><AdminAiSpendPage /></AdminRoute>} />
          <Route path="/admin/scoring" element={<AdminRoute><AdminScoringPage /></AdminRoute>} />
          <Route path="/admin/chat-dead-ends" element={<AdminRoute><AdminChatDeadEndsPage /></AdminRoute>} />
          <Route path="/admin/templates/upload" element={<AdminRoute><AdminTemplateUploadPage /></AdminRoute>} />
          <Route path="/admin/legal-documents" element={<AdminRoute><AdminLegalDocumentsPage /></AdminRoute>} />
          <Route path="/admin/mls-catalog" element={<AdminRoute><AdminMlsCatalogPage /></AdminRoute>} />
          <Route path="/admin/system-status" element={<AdminRoute><AdminSystemStatusPage /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPaymentsPage /></AdminRoute>} />
          <Route path="/admin/payments/:userId" element={<AdminRoute><AdminAccountBillingPage /></AdminRoute>} />

          {/* Root (superadmin) routes — silently redirect non-superadmin to /dashboard */}
          <Route path="/root/users" element={<SuperAdminRoute><RootUsers /></SuperAdminRoute>} />
          <Route path="/root/users/:id" element={<SuperAdminRoute><RootUserDetail /></SuperAdminRoute>} />
          <Route path="/root/websites" element={<SuperAdminRoute><RootWebsites /></SuperAdminRoute>} />
          <Route path="/root/templates" element={<SuperAdminRoute><RootTemplates /></SuperAdminRoute>} />
          <Route path="/root/payments" element={<SuperAdminRoute><RootPayments /></SuperAdminRoute>} />
          <Route path="/root/wallets" element={<SuperAdminRoute><RootWallets /></SuperAdminRoute>} />
          <Route path="/root/site-looks" element={<SuperAdminRoute><RootSiteLooks /></SuperAdminRoute>} />
          <Route path="/root/plans" element={<SuperAdminRoute><RootPlans /></SuperAdminRoute>} />
          <Route path="/root/requests" element={<SuperAdminRoute><RootChangeRequests /></SuperAdminRoute>} />
        </Route>

        {/* The domain's own pages — what the product is, and the platform's legal documents. A
            signed-in user never sees the landing page; the root sends them on to the dashboard. */}
        <Route path="/" element={<RedirectIfAuth><WelcomePage /></RedirectIfAuth>} />
        <Route path="/privacy" element={<PublicLegalPage kind="privacy" />} />
        <Route path="/terms" element={<PublicLegalPage kind="terms" />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
    </AuthGate>
  );
}
